import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps } from 'bernie-l10n';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { PriceSummary } from '@shared-ui/pricing-price-summary';
import { useLocalization } from '@shared-ui/localization-context';

import { BadgeModel, PriceViewModel } from 'models/common';
import { RecentlyViewedModel } from 'models';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';

import { BadgeGenerator } from 'components/common/badge-generation';
import { CommonPropertyCardFields } from '__generated__/api';
import { MarketingFeeDetailsContainer } from 'components/common/pricing/marketing-fee-details-container';
import { Price } from 'components/common/pricing/price';
import { PriceLockup } from 'components/common/pricing/price-lockup';

// Type guard for PriceSummaryData
type PriceSummaryData = React.ComponentProps<typeof PriceSummary>['data'];
const isPriceSummaryProps = (price: PriceViewModel | CommonPropertyCardFields.Price): price is CommonPropertyCardFields.Price =>
  Boolean(price && 'displayMessages' in price && price.displayMessages.length);

export interface PriceAndSupportingMessagesContainerProps extends LocalizedComponentProps {
  index?: number;
  secondaryCardMessage?: string;
  price?: PriceViewModel | CommonPropertyCardFields.Price;
  supportingMessages?: string[];
  viewSmall: boolean;
  wrapPrice: boolean;
  priceFontWeight: string;
  hotelId?: number;
  secondary?: BadgeModel;
  recentlyViewed?: RecentlyViewedModel;
  infositeUrl?: string;
  history?: {
    push: (submitUrl: string) => void;
  };
}

export const PriceAndSupportingMessagesContainer: React.FunctionComponent<PriceAndSupportingMessagesContainerProps> = ({
  index,
  secondaryCardMessage,
  supportingMessages,
  price,
  viewSmall,
  wrapPrice,
  priceFontWeight,
  hotelId,
  secondary,
  recentlyViewed,
  infositeUrl,
}) => {
  const l10n = useLocalization();

  if (isPriceSummaryProps(price)) {
    const marketingFeeDetailsProps = {
      marketingFeeDialogId: hotelId ? `priceMarketingFeeDialog-${hotelId}` : 'priceMarketingFeeDialog',
      marketingFeeDetails: price?.marketingFeeDetails,
    };

    return viewSmall ? (
      <>
        <PriceSummary data={price as PriceSummaryData} />
        <MarketingFeeDetailsContainer {...marketingFeeDetailsProps} />
      </>
    ) : (
      <UitkLayoutFlex direction="column" alignItems="end">
        <UitkLayoutFlexItem>
          {secondary && (secondary.text || secondary.icon) && <BadgeGenerator badge={secondary} uniqueId={hotelId} />}
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem>
          <PriceSummary data={price as PriceSummaryData} />
        </UitkLayoutFlexItem>
        <UitkLayoutFlexItem>
          <MarketingFeeDetailsContainer {...marketingFeeDetailsProps} />
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    );
  }

  const priceCommonProps = {
    index,
    price,
    hotelId,
    priceFontWeight,
    recentlyViewed,
    messages: supportingMessages,
  };

  const priceLockupProps = {
    price,
    uniqueId: hotelId,
    supportingMessages,
    infositeUrl,
    stids: {
      lockupId: 'display-price',
      strikeThroughId: 'strikeout-price',
      subtextFirstId: 'price-messages',
      subtextStandardId: 'supporting-messages',
    },
    l10n,
  };

  if (viewSmall) {
    return (
      <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink listing__pricing s-t-padding-half">
        {secondaryCardMessage && (
          <span className="secondary-card-message uitk-type-200 uitk-type-medium pwa-theme--grey-900">{secondaryCardMessage}</span>
        )}
        <Experiment name="SRP_UITK_PriceLockup_Migration">
          <ExperimentControl>
            <ErrorBoundary label="hotel.search.listing.price">
              <Price className="listing__price" wrapPrice={wrapPrice} {...priceCommonProps} />
            </ErrorBoundary>
          </ExperimentControl>
          <ExperimentVariant bucket={1}>
            <ErrorBoundary label="hotel.search.listing.priceLockup">
              <PriceLockup {...priceLockupProps} />
            </ErrorBoundary>
          </ExperimentVariant>
        </Experiment>
      </div>
    );
  }

  return (
    <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink">
      {secondary && (secondary.text || secondary.icon) && <BadgeGenerator badge={secondary} uniqueId={hotelId} />}
      {secondaryCardMessage && (
        <div className="secondary-card-message uitk-type-200 uitk-type-medium pwa-theme--grey-900 all-t-margin-one">
          {secondaryCardMessage}
        </div>
      )}{' '}
      <Experiment name="SRP_UITK_PriceLockup_Migration">
        <ExperimentControl>
          <ErrorBoundary label="hotel.search.listing.price">
            <Price className="listing__price all-t-padding-half" {...priceCommonProps} />
          </ErrorBoundary>
        </ExperimentControl>
        <ExperimentVariant bucket={1}>
          <ErrorBoundary label="hotel.search.listing.priceLockup">
            <PriceLockup {...priceLockupProps} />
          </ErrorBoundary>
        </ExperimentVariant>
      </Experiment>
    </div>
  );
};
